/**
 * Hook pour charger les configurations école (school_schedules)
 * Utilisé dans l'agenda pour afficher les jours de formation en rose
 */

import { useEffect, useState, useRef } from 'react';
import { cacheGet, cacheSet, emitCacheUpdated } from '../utils/cache';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

/**
 * Hook pour charger toutes les configurations école
 * Cache les données pour éviter rechargements
 */
export function useSchoolSchedules() {
  const [state, setState] = useState(() => ({
    schools: cacheGet('school/schedules', []),
    loading: false,
    error: null
  }));

  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadSchools = async () => {
      // Si déjà en cache, utiliser
      const cached = cacheGet('school/schedules', null);
      if (cached && cached.length > 0) {
        console.log('⚡ [useSchoolSchedules] Chargement depuis cache');
        return;
      }

      // 🔥 FIX: Annuler toute requête précédente
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      if (isMountedRef.current) {
        setState(s => ({ ...s, loading: true }));
      }

      try {
        const response = await fetch(API_ENDPOINTS.SCHOOL_LIST(), {
          headers: getAuthHeaders(),
          credentials: 'include',
          signal
        });

        // 🔥 FIX: Vérifier si la requête a été annulée
        if (signal.aborted || !isMountedRef.current) {
          return;
        }

        if (!response.ok) {
          if (response.status === 404) {
            // Pas de configurations école
            cacheSet('school/schedules', []);
            if (isMountedRef.current) {
              setState({ schools: [], loading: false, error: null });
            }
            return;
          }
          throw new Error(`Erreur ${response.status}`);
        }

        const schools = await response.json();
        
        // 🔥 FIX: Vérifier à nouveau après l'async
        if (signal.aborted || !isMountedRef.current) {
          return;
        }
        
        // Normaliser les données
        const normalized = schools.map(school => ({
          id: school.id,
          operator_id: school.operator_id,
          employee_id: school.employee_id,
          weekly_hours: school.weekly_hours,
          school_days: school.school_days, // Array: ['LUNDI', 'MARDI', ...]
          school_start_time: school.school_start_time,
          contract_start_date: school.contract_start_date,
          contract_end_date: school.contract_end_date,
          contract_type: school.contract_type
        }));

        cacheSet('school/schedules', normalized);
        emitCacheUpdated('school/schedules');
        
        // 🔥 FIX: Ne mettre à jour que si le composant est encore monté
        if (isMountedRef.current) {
          setState({ schools: normalized, loading: false, error: null });
          console.log(`✅ [useSchoolSchedules] Chargé ${normalized.length} configurations école`);
        }
      } catch (err) {
        // 🔥 FIX: Ignorer les erreurs d'abort
        if (err.name === 'AbortError' || signal.aborted) {
          return;
        }
        
        console.error('❌ [useSchoolSchedules] Erreur:', err);
        if (isMountedRef.current) {
          setState(s => ({ ...s, loading: false, error: err.message }));
        }
      }
    };

    loadSchools();
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Écouter les mises à jour
  const isMountedListenerRef = useRef(true);
  
  useEffect(() => {
    isMountedListenerRef.current = true;
    
    const onUpd = (e) => {
      if (e?.detail?.topic === 'school/schedules' && isMountedListenerRef.current) {
        const cached = cacheGet('school/schedules', []);
        if (isMountedListenerRef.current) {
          setState(s => ({ ...s, schools: cached }));
        }
      }
    };

    window.addEventListener('cc:cache-updated', onUpd);
    return () => {
      isMountedListenerRef.current = false;
      window.removeEventListener('cc:cache-updated', onUpd);
    };
  }, []);

  return state;
}

/**
 * Mapper jour français vers numéro (pour correspondance)
 */
const DAY_MAP = {
  'LUNDI': 1,
  'MARDI': 2,
  'MERCREDI': 3,
  'JEUDI': 4,
  'VENDREDI': 5,
  'SAMEDI': 6,
  'DIMANCHE': 7
};

/**
 * Vérifie si un opérateur a formation ce jour-là
 * @param {Array} schools - Liste des configurations école
 * @param {number} operatorId - ID de l'opérateur
 * @param {Date} date - Date à vérifier
 * @returns {Object|null} Info formation ou null
 */
export function getSchoolForDay(schools, operatorId, date) {
  if (!schools || !Array.isArray(schools)) return null;
  
  // Trouver la config de cet opérateur
  const school = schools.find(s => Number(s.operator_id) === Number(operatorId));
  if (!school) return null;
  
  // Vérifier que la date est dans la période de contrat
  const dateStr = date.toISOString().split('T')[0];
  if (school.contract_start_date && dateStr < school.contract_start_date) return null;
  if (school.contract_end_date && dateStr > school.contract_end_date) return null;
  
  // Vérifier si ce jour de la semaine est un jour d'école
  const dayOfWeek = date.getDay();
  const weekday = dayOfWeek === 0 ? 7 : dayOfWeek; // Dim=7, Lun=1, etc.
  
  // school_days peut être un array de strings ['LUNDI', 'MERCREDI'] 
  // ou un JSON string
  let schoolDays = school.school_days;
  if (typeof schoolDays === 'string') {
    try {
      schoolDays = JSON.parse(schoolDays);
    } catch {
      return null;
    }
  }
  
  if (!Array.isArray(schoolDays)) return null;
  
  // Vérifier si ce weekday est dans school_days
  const isSchoolDay = schoolDays.some(day => {
    const dayNum = DAY_MAP[day];
    return dayNum === weekday;
  });
  
  if (!isSchoolDay) return null;
  
  // Retourner les infos de formation
  const startTime = school.school_start_time || '08:30';
  const endTime = calculateEndTime(startTime, school.weekly_hours / schoolDays.length);
  
  return {
    type: 'school',
    start: startTime.slice(0, 5), // HH:MM
    end: endTime,
    weekly_hours: school.weekly_hours,
    label: 'Formation'
  };
}

/**
 * Calcule l'heure de fin basée sur durée
 * @param {string} startTime - Heure de début HH:MM:SS
 * @param {number} durationHours - Durée en heures
 * @returns {string} Heure de fin HH:MM
 */
function calculateEndTime(startTime, durationHours) {
  const [h, m] = startTime.split(':').map(Number);
  const totalMinutes = h * 60 + m + Math.round(durationHours * 60);
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}
